import { ScrollButton } from "@/features/MyClub/Recommendations/ScrollElements";
import { pauseIcon, playIcon } from "@/figmaElements";
import { cn } from "@/lib/utils";
import { userSubscriptionSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { MyClubBanner } from "./MyClubBanner/MyClubBanner";
import { MyEvoBanner } from "./MyEvoBanner/MyEvoBanner";
import { SBCSolverBanner } from "./SBCSolverBanner/SBCSolverBanner";
import { SquadBuilderBanner } from "./SquadBuilderBanner/SquadBuilderBanner";

const TRANSITION_DURATION = 500;
const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe to be registered

export const RootBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isProcessingTouchRef = useRef(false);
  const subscription = useSelector(userSubscriptionSelector);
  const isAutoPlayRef = useRef(isAutoPlay);

  useEffect(() => {
    isAutoPlayRef.current = isAutoPlay;
  }, [isAutoPlay]);

  const handleLeftClick = useCallback(() => {
    setCurrentIndex((prev) => {
      // Ensure we don't go below 0, wrap around to the end instead
      const newIndex = prev - 1;
      return newIndex < 0 ? 6 : newIndex; // 6 is images.length - 1 based on the structure
    });
    stopInterval();
    if (isAutoPlayRef.current) {
      startInterval();
    }
  }, [isAutoPlay]);

  const handleRightClick = useCallback(() => {
    setCurrentIndex((prev) => {
      // Ensure we don't go above the maximum index, wrap around to the beginning instead
      const newIndex = prev + 1;
      return newIndex > 6 ? 0 : newIndex; // 6 is images.length - 1 based on the structure
    });
    stopInterval();
    if (isAutoPlayRef.current) {
      startInterval();
    }
  }, []);

  // Touch gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current.x || !touchEndRef.current.x) return;

    // Prevent rapid swiping by checking if we're already processing a touch
    if (isProcessingTouchRef.current) return;

    const deltaX = touchStartRef.current.x - touchEndRef.current.x;
    const deltaY = Math.abs(touchStartRef.current.y - touchEndRef.current.y);

    // Only register horizontal swipes if vertical movement is minimal
    if (Math.abs(deltaX) > SWIPE_THRESHOLD && deltaY < SWIPE_THRESHOLD) {
      // Set processing flag to prevent rapid swipes
      isProcessingTouchRef.current = true;

      if (deltaX > 0) {
        // Swipe left - go to next
        handleRightClick();
      } else {
        // Swipe right - go to previous
        handleLeftClick();
      }

      // Reset processing flag after the transition duration to allow the next swipe
      setTimeout(() => {
        isProcessingTouchRef.current = false;
      }, TRANSITION_DURATION + 100);
    }

    // Reset touch references
    touchStartRef.current = { x: 0, y: 0 };
    touchEndRef.current = { x: 0, y: 0 };
  }, [handleLeftClick, handleRightClick]);

  const originalImages = useMemo(() => {
    const baseBanners: { image: React.ReactNode; id?: string }[] = [
      {
        image: (
          <MyClubBanner
            onClickLeft={handleLeftClick}
            onClickRight={handleRightClick}
          />
        ),
        id: "my-club-banner",
      },
      {
        image: (
          <SquadBuilderBanner
            onClickLeft={handleLeftClick}
            onClickRight={handleRightClick}
          />
        ),
        id: "squad-builder-banner",
      },
      {
        image: (
          <SBCSolverBanner
            onClickLeft={handleLeftClick}
            onClickRight={handleRightClick}
          />
        ),
        id: "sbc-solver-banner",
      },
      {
        image: (
          <MyEvoBanner
            onClickLeft={handleLeftClick}
            onClickRight={handleRightClick}
          />
        ),
        id: "my-evo-banner",
      },
    ];
    return baseBanners;
  }, []);

  const shuffledImages = useMemo(() => {
    const arr = [...originalImages];
    if (originalImages.length === 1) {
      return arr;
    }
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    const shuffledArr = [...arr, ...arr.slice(0, 2)];
    return shuffledArr;
  }, [subscription]);

  const images = shuffledImages;

  const startInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        // Ensure we don't go above the maximum index, wrap around to the beginning instead
        const newIndex = prevIndex + 1;
        return newIndex > 6 ? 0 : newIndex; // 6 is images.length - 1 based on the structure
      });
    }, 6000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleDotClick = (index: number) => {
    stopInterval();
    setCurrentIndex(index + 1);
    if (isAutoPlay) {
      startInterval();
    }
  };

  useEffect(() => {
    startInterval();
    return () => stopInterval();
  }, []);

  // Handle infinite loop transitions
  useEffect(() => {
    if (currentIndex === 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(images.length - 2);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
    if (currentIndex === images.length - 1) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(1);
      }, TRANSITION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, images.length]);

  // Safety check to ensure currentIndex is always within bounds
  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= images.length) {
      setCurrentIndex(1);
    }
  }, [currentIndex, images.length]);
  // Re-enable transitions after infinite loop jump
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  if (images.length === 1) {
    return images[0].image;
  }
  return (
    <div className="mx-auto w-full max-w-content relative">
      <div
        className="relative overflow-hidden shadow-2xl w-full"
        onTouchStart={isTouchDevice ? handleTouchStart : undefined}
        onTouchMove={isTouchDevice ? handleTouchMove : undefined}
        onTouchEnd={isTouchDevice ? handleTouchEnd : undefined}
      >
        <div
          className="flex"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isTransitioning
              ? `transform ${TRANSITION_DURATION}ms ease-in-out`
              : "none",
          }}
        >
          {images.map((banner, index) => (
            <div className="w-full shrink-0 object-cover" key={index}>
              {banner.image}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 my-4 justify-center gap-2 hidden touch-device:flex">
        <button
          className="flex gap-x-[3px]"
          onClick={() => {
            if (isAutoPlay) {
              setIsAutoPlay(false);
              stopInterval();
            } else {
              setIsAutoPlay(true);
              startInterval();
            }
          }}
        >
          {isAutoPlay ? pauseIcon : playIcon}
        </button>
        {originalImages.map((_, index) => {
          const activeIndex =
            (currentIndex - 1 + originalImages.length) % originalImages.length;
          return (
            <button
              className="h-3.5"
              onClick={() => handleDotClick(index)}
              key={index}
            >
              <div
                className={cn(
                  "w-10 rounded-full transition-all duration-500 ease-in-out h-1",
                  activeIndex === index
                    ? "bg-primary-action"
                    : "bg-new-gray-450",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CTAButton = ({
  onClick,
  label,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
}) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className="font-oswald mx-auto w-[280px] h-[56px] rounded bg-primary-dark-blue text-white font-bold text-xl transition-all duration-200 ease-in-out transform hover:scale-102 hover:bg-primary-blue active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-dark-blue/40"
    >
      {label}
    </button>
  );
};

export const BannerStructure = ({
  title,
  subtitle,
  children,
  bgUrl,
  bgColor,
  bgGradient,
  learnMoreLink,
  onClickCTA,
  onClickLeft,
  onClickRight,
  CTAButtonLabel,
  hideCTAButton,
  alignRight,
  newDesign,
}: {
  children: React.ReactNode;
  bgUrl?: string;
  bgColor?: string;
  bgGradient?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  learnMoreLink?: string;
  onClickCTA: () => void;
  onClickLeft?: () => void;
  onClickRight?: () => void;
  subscriptionLabel?: string;
  CTAButtonLabel?: string;
  hideCTAButton?: boolean;
  alignRight?: boolean;
  newDesign?: boolean;
}) => {
  const { t } = useTranslation();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const handleOnClickCta = () => {
    onClickCTA();
  };

  if (isTouchDevice) {
    return (
      <article
        className={cn(
          "w-full relative bg-cover bg-center bg-no-repeat flex flex-col items-center h-[250px] touch-device:h-[360px] my-auto justify-center text-center",
        )}
        style={{
          backgroundImage: `url(${bgUrl})`,
          background: bgGradient,
          backgroundColor: bgColor,
        }}
      >
        {children}
        <div
          className={cn(
            "flex flex-col justify-center items-center gap-y-4 mb-3 z-10",
            newDesign ? "w-[332px]" : "w-[350px]",
          )}
          // className={cn(
          //   "flex flex-col justify-center items-center gap-y-4 mb-3 z-10",
          //   newDesign ? "w-[332px]" : "w-[350px]",
          // )}
        >
          {!newDesign && (
            <img
              src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
              alt={t("brand")}
              className="hidden touch-device:block"
            />
          )}
          <div className="flex flex-col gap-y-2">
            <p
              className={cn(
                newDesign
                  ? "font-oswald text-[30px] font-normal"
                  : "text-base font-bold text-center max-w-74 mx-auto",
              )}
            >
              {title}
            </p>
            {subtitle && (
              <p
                className={cn(
                  newDesign ? "text-lg" : "text-lg font-bold text-center",
                )}
              >
                {subtitle}
              </p>
            )}

            {learnMoreLink && (
              <NavLink
                className="text-base text-center text-dark-tertiary italic underline w-fit mx-auto"
                to={learnMoreLink}
                state={{ from: "home" }}
              >
                {t("learnMore")}
              </NavLink>
            )}
            {!hideCTAButton && (
              <CTAButton onClick={handleOnClickCta} label={CTAButtonLabel} />
            )}
          </div>
        </div>
      </article>
    );
  } else {
    return (
      <article
        className={cn(
          "w-full relative bg-cover bg-center bg-no-repeat flex flex-col overflow-hidden group h-90 max-w-content mx-auto",
          alignRight ? "bg-right" : "bg-center",
        )}
        style={{
          backgroundImage: `url(${bgUrl})`,
          backgroundColor: bgColor,
        }}
      >
        {onClickLeft && onClickRight && (
          <>
            <div className="hidden touch-device:block absolute top-1/2 -translate-y-1/2 left-4 z-1 group-hover:opacity-100 opacity-0 transition-opacity duration-300">
              <ScrollButton direction="left" onClick={onClickLeft} />
            </div>
            <div className="hidden touch-device:block absolute top-1/2 -translate-y-1/2 right-4 z-1 group-hover:opacity-100 opacity-0 transition-opacity duration-300">
              <ScrollButton direction="right" onClick={onClickRight} />
            </div>
          </>
        )}
        {children}
        <div
          className={cn(
            "flex flex-col justify-between z-1",
            newDesign
              ? "w-[520px] ml-[90px] my-auto gap-y-6"
              : "w-[380px] ml-[130px] h-[228px] mt-[80px] mb-[52px] ",
          )}
        >
          {!newDesign && (
            <img
              src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
              alt={t("brand")}
              className="h-[50px] w-auto mx-auto hidden touch-device:block"
            />
          )}
          <div className="flex flex-col">
            <p
              className={cn(
                newDesign
                  ? "font-oswald text-[36px] leading-[50px] font-semibold"
                  : "text-lg font-bold text-center max-w-84 mx-auto",
              )}
            >
              {title}
            </p>
            {subtitle && (
              <p
                className={cn(
                  "mt-4.5",
                  newDesign ? "text-xl" : "text-lg font-bold text-center",
                )}
              >
                {subtitle}
              </p>
            )}
            {learnMoreLink && (
              <NavLink
                className="text-base text-center text-dark-tertiary italic underline w-fit mr-auto"
                to={learnMoreLink}
                state={{ from: "home" }}
              >
                {t("learnMore")}
              </NavLink>
            )}
          </div>
          <div
            className={cn(
              hideCTAButton && "invisible",
              newDesign ? "mr-auto" : "mx-auto",
            )}
          >
            <CTAButton onClick={handleOnClickCta} label={CTAButtonLabel} />
          </div>
        </div>
      </article>
    );
  }
};
